import React, {useMemo, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {
  adminTagsCache,
  categoriesWithTagsCache,
  recommendationRebuildCache,
} from '../../../common/constants/queryCacheKey';
import {
  AdminTag,
  createTag,
  CreateTagInput,
  deleteTag,
  fetchAdminTags,
} from '../../../common/services/tags';
import {RebuildStatusBanner} from '../../admin/RebuildStatusBanner';
import {useGuardedMutate} from '../../../common/hooks/useGuardedMutate';
import {
  badgeStyle,
  dangerButtonStyle,
  disabledButtonStyle,
  errorStyle,
  inputStyle,
  labelStyle,
  messageStyle,
  noticeStyle,
  panelStyle,
  primaryButtonStyle,
  screenStyle,
  titleStyle,
} from '../../admin/adminStyles';

const ALL_CATEGORIES = 0;

const usageText = (tag: AdminTag) =>
  `${tag.placeCount} places · ${tag.eventCount} events · ${tag.userCount} users`;

const deleteConfirmation = (tag: AdminTag) => {
  const lines = [
    `Delete the tag "${tag.name}"?`,
    '',
    `It is removed from ${tag.placeCount} places, ${tag.eventCount} events and the preferences of ${tag.userCount} users.`,
  ];
  if (tag.linkedTo.length > 0) {
    lines.push(`It is no longer part of: ${tag.linkedTo.join(', ')}.`);
  }
  if (tag.usedByTripPlanner) {
    lines.push('The trip planner uses it in its groups; they lose it.');
  }
  lines.push(
    '',
    'Its weight is cleared from every recommendation vector and a full rebuild starts. This cannot be undone.',
  );
  return lines.join('\n');
};

export const TagsScreen: React.FC = () => {
  const queryClient = useQueryClient();

  const [filter, setFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(ALL_CATEGORIES);
  const [isAdding, setIsAdding] = useState(false);
  const [newTag, setNewTag] = useState<CreateTagInput>({
    name: '',
    categoryCode: 0,
    isForGoogleSearch: true,
  });
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const tagsQuery = useQuery({
    queryKey: [adminTagsCache],
    queryFn: fetchAdminTags,
  });

  const refresh = () => {
    queryClient.invalidateQueries({queryKey: [adminTagsCache]});
    // The place form and the old tag picker read this list.
    queryClient.invalidateQueries({queryKey: [categoriesWithTagsCache]});
  };

  const createMutation = useMutation({
    mutationFn: createTag,
    onSuccess: tag => {
      setIsAdding(false);
      setNewTag(current => ({...current, name: ''}));
      setActionError(null);
      setNotice(
        `"${tag.name}" was added (code ${tag.code}).${
          tag.isForGoogleSearch
            ? ' The places importer searches Google for it on its next run.'
            : ''
        }`,
      );
      refresh();
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteTag,
    onSuccess: result => {
      setActionError(null);
      setNotice(
        `"${result.name}" was deleted and cleared from ${result.vectorsCleared} vectors. The recommendation rebuild has started.`,
      );
      refresh();
      queryClient.invalidateQueries({queryKey: [recommendationRebuildCache]});
    },
    onError: (error: Error) => setActionError(error.message),
  });

  const data = tagsQuery.data;
  const categories = useMemo(() => data?.categories ?? [], [data]);

  const groups = useMemo(() => {
    const keyword = filter.trim().toLowerCase();
    const tags = (data?.tags ?? []).filter(
      tag =>
        (categoryFilter === ALL_CATEGORIES ||
          tag.categoryCode === categoryFilter) &&
        (!keyword || tag.name.toLowerCase().includes(keyword)),
    );

    return categories
      .map(category => ({
        category,
        tags: tags.filter(tag => tag.categoryCode === category.code),
      }))
      .filter(group => group.tags.length > 0);
  }, [data, categories, filter, categoryFilter]);

  const shownCount = groups.reduce((sum, group) => sum + group.tags.length, 0);

  const create = useGuardedMutate(createMutation.mutate);

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    create({...newTag, name: newTag.name.trim()});
  };

  const handleDelete = (tag: AdminTag) => {
    if (window.confirm(deleteConfirmation(tag))) {
      setNotice(null);
      deleteMutation.mutate(tag.code);
    }
  };

  const openAddForm = () => {
    setIsAdding(current => !current);
    setNewTag(current => ({
      ...current,
      categoryCode:
        current.categoryCode ||
        (categoryFilter !== ALL_CATEGORIES
          ? categoryFilter
          : (categories[0]?.code ?? 0)),
    }));
  };

  return (
    <div style={screenStyle}>
      <div style={{maxWidth: '900px', margin: '0 auto'}}>
        <h1 style={titleStyle}>Tags</h1>

        <RebuildStatusBanner />

        <div style={toolbarStyle}>
          <input
            placeholder="Search tags..."
            value={filter}
            onChange={e => setFilter(e.target.value)}
            style={{...inputStyle, flex: '1 1 220px', width: 'auto'}}
          />
          <select
            value={categoryFilter}
            onChange={e => setCategoryFilter(Number(e.target.value))}
            style={{...inputStyle, width: '240px', maxWidth: '100%'}}>
            <option value={ALL_CATEGORIES}>All categories</option>
            {categories.map(category => (
              <option key={category.code} value={category.code}>
                {category.name}
              </option>
            ))}
          </select>
          <button
            onClick={openAddForm}
            disabled={!data || data.remainingCodes <= 0}
            style={
              !data || data.remainingCodes <= 0
                ? disabledButtonStyle
                : primaryButtonStyle
            }>
            {isAdding ? 'Cancel' : 'Add tag'}
          </button>
        </div>

        {isAdding && (
          <form onSubmit={handleCreate} style={{...panelStyle, ...formStyle}}>
            <label style={{...labelStyle, flex: 2, minWidth: '200px'}}>
              Name
              <input
                required
                autoFocus
                maxLength={50}
                placeholder="e.g. Rooftop bar"
                value={newTag.name}
                onChange={e => setNewTag({...newTag, name: e.target.value})}
                style={inputStyle}
              />
            </label>
            <label style={{...labelStyle, flex: 1, minWidth: '200px'}}>
              Category
              <select
                required
                value={newTag.categoryCode}
                onChange={e =>
                  setNewTag({...newTag, categoryCode: Number(e.target.value)})
                }
                style={inputStyle}>
                {categories.map(category => (
                  <option key={category.code} value={category.code}>
                    {category.name}
                  </option>
                ))}
              </select>
            </label>
            <label style={checkboxLabelStyle}>
              <input
                type="checkbox"
                checked={newTag.isForGoogleSearch}
                onChange={e =>
                  setNewTag({...newTag, isForGoogleSearch: e.target.checked})
                }
              />
              Search Google for places with this tag
            </label>
            <button
              type="submit"
              disabled={createMutation.isPending}
              style={primaryButtonStyle}>
              {createMutation.isPending ? 'Adding...' : 'Add'}
            </button>
            <p style={hintStyle}>
              The name is also the Google search the places importer runs, so
              write it the way people search for it. Untick the box for
              attribute tags (like "Good for groups") that are not a kind of
              place. {data ? `${data.remainingCodes} tag codes left.` : ''}
            </p>
          </form>
        )}

        {actionError && <p style={errorStyle}>{actionError}</p>}
        {notice && <p style={noticeStyle}>{notice}</p>}

        <p style={{color: '#999', margin: '0 0 16px'}}>
          {tagsQuery.isError
            ? ''
            : tagsQuery.isPending
            ? 'Loading...'
            : `${shownCount} of ${data?.tags.length ?? 0} tags`}
        </p>

        {groups.map(({category, tags}) => (
          <section key={category.code} style={{marginBottom: '28px'}}>
            <h2 style={categoryTitleStyle}>
              {category.name}{' '}
              <span style={{color: '#666', fontWeight: 'normal'}}>
                ({tags.length})
              </span>
            </h2>
            <div style={{display: 'flex', flexDirection: 'column', gap: '8px'}}>
              {tags.map(tag => (
                <div key={tag.code} style={rowStyle}>
                  <div style={{flex: 1, minWidth: 0}}>
                    <div style={nameStyle}>
                      {tag.name}
                      <span style={codeStyle}>#{tag.code}</span>
                    </div>
                    <div style={metaStyle}>
                      {usageText(tag)}
                      {tag.linkedTo.length > 0
                        ? ` · ${tag.linkedTo.join(', ')}`
                        : ''}
                    </div>
                  </div>
                  {!tag.isForGoogleSearch && (
                    <span style={badgeStyle} title="Not searched on Google">
                      Attribute
                    </span>
                  )}
                  {tag.usedByTripPlanner && (
                    <span style={badgeStyle}>Trip planner</span>
                  )}
                  <button
                    onClick={() => handleDelete(tag)}
                    disabled={
                      !!tag.deleteBlockedReason || deleteMutation.isPending
                    }
                    title={tag.deleteBlockedReason ?? ''}
                    style={
                      tag.deleteBlockedReason
                        ? disabledButtonStyle
                        : dangerButtonStyle
                    }>
                    Delete
                  </button>
                </div>
              ))}
            </div>
          </section>
        ))}

        {tagsQuery.isError && (
          <p style={messageStyle}>Could not load tags. Try again.</p>
        )}
        {!tagsQuery.isError && !tagsQuery.isPending && shownCount === 0 && (
          <p style={messageStyle}>No tags match your search.</p>
        )}
      </div>
    </div>
  );
};

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: '12px',
  marginBottom: '20px',
};

const formStyle: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'flex-end',
  gap: '12px',
  marginBottom: '20px',
};

const checkboxLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  color: '#ddd',
  fontSize: '0.85rem',
  height: '40px',
};

const hintStyle: React.CSSProperties = {
  color: '#999',
  margin: 0,
  flexBasis: '100%',
  fontSize: '0.8rem',
};

const categoryTitleStyle: React.CSSProperties = {
  color: '#fff',
  fontSize: '1.05rem',
  margin: '0 0 10px',
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  padding: '10px 14px',
  borderRadius: '12px',
  backgroundColor: '#242424',
  border: '1px solid #333',
};

const nameStyle: React.CSSProperties = {
  color: '#fff',
  fontWeight: 'bold',
};

const codeStyle: React.CSSProperties = {
  color: '#666',
  fontWeight: 'normal',
  fontSize: '0.8rem',
  marginLeft: '8px',
};

const metaStyle: React.CSSProperties = {
  color: '#999',
  fontSize: '0.8rem',
  marginTop: '2px',
};
